import {
  buildResourceServerPayloadArray,
  buildResourceServerPayloadEntry,
  DEFAULT_CATALOGUE_RESOURCE_SERVER_ID,
  inferResourceServerIdFromValue,
  normalizeResourceServerHostUrl,
  normalizeResourceServerId,
  resolveResourceServerAccessTypes,
  resolveResourceServerUrlConfig,
  resolveCatalogueApdUrl,
} from '@angular-workspace/feature';
import { AuthApiService } from '@angular-workspace/services';
import { environment } from '../../../environments/environment';
import { FormatService } from '../services/format.service';

export const FILE_RESOURCE_SERVER_VALUE = 'FILE';

export interface OrganizationContext {
  userOrganisationId: string;
  organisationName: string;
  organisationType: string;
  industry: string;
  department: string;
  uploadedBy: string;
  ownerUserId: string;
}

export interface AssetDescriptorInput {
  attributeName: string;
  parameterType: string;
  schemaType: string;
  description?: string;
  unitCode?: string;
  unitText?: string;
}

export interface AssetDescriptorPayloadEntry {
  parameterType: string;
  schemaType: string;
  description?: string;
  unitCode?: string;
  unitText?: string;
}

export interface AssetCatalogueFormData {
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  description?: string;
  tags?: string[] | string;
  permission?: string;
  license?: string;
  otherLicense?: string;
  industry?: string;
  datasetType?: string;
  modelType?: string;
  fileFormat?: string;
  sampleDataUrl?: string;
  resourceServer?: string | unknown;
  yearRange?: string | { startYear: number | string; endYear: number | string };
  geoCoverage?: string;
  uploadFrequency?: string;
  concepts?: string[];
  dataSample?: unknown;
  dataDescriptors?: AssetDescriptorInput[] | Record<string, AssetDescriptorPayloadEntry>;
  data_descriptor?: unknown;
  jsonSchema?: string | Record<string, unknown>;
}

export interface AssetCatalogueOriginalData {
  type?: string | string[];
  name?: string;
  organizationId?: string;
  organizationType?: string;
  organization?: string;
  department?: string;
  uploadedBy?: string;
  ownerUserId?: string;
  owner_user_id?: string;
  modelType?: string;
  fileFormat?: string;
  fileSize?: string;
  file_size?: string;
  mediaURL?: string;
  media_url?: string;
  sampleDataUrl?: string;
  shortDescription?: string;
  description?: string;
  concepts?: string[];
  verifiedBy?: string;
  yearRange?: string | { startYear: number | string; endYear: number | string };
  geoCoverage?: string;
  geo_coverage?: string;
  uploadFrequency?: string;
  upload_frequency?: string;
  dataBankType?: string;
  datasetType?: string;
  dataSample?: unknown;
  data_sample?: unknown;
  resourceServer?: string | unknown;
  resource_server?: string;
  industry?: string;
  dataDescriptor?: unknown;
  data_descriptor?: unknown;
  dataDescriptors?: unknown;
}

export interface BuildAssetCataloguePayloadOptions {
  formData: AssetCatalogueFormData;
  isModel: boolean;
  organizationContext: OrganizationContext;
  assetId?: string;
  originalData?: AssetCatalogueOriginalData;
  dataDescriptors?: AssetDescriptorInput[];
  /** Mapped preview payload from modify modal (carries normalized descriptor rows). */
  assetPreviewData?: Record<string, unknown>;
  /** When false, model payloads omit mediaURL unless explicitly provided. */
  includeModelDataSourceUrl?: boolean;
  defaultModelMediaUrl?: string;
}

export async function resolveOrganizationContext(
  authApiService: AuthApiService,
): Promise<OrganizationContext> {
  let userData: any = null;
  let organizationsList: any[] = [];

  try {
    userData = await authApiService.getUserInfo();
    const orgsResponse = await authApiService.getAllOrganisations();
    organizationsList = orgsResponse?.result || orgsResponse?.data || [];
  } catch (error) {
    console.error('Error fetching user/organization info:', error);
  }

  const result = userData?.result || userData || {};
  const organisation = result.organisation || {};
  const userOrganisationId = organisation.organisationId || organisation.id || '';

  let organisationName = '';
  let organisationType = 'Public';
  let industry = 'forest';
  let department = '';

  if (userOrganisationId && organizationsList.length > 0) {
    const matchedOrg = organizationsList.find(
      (org: any) => org.id === userOrganisationId || org.organisationId === userOrganisationId,
    );
    if (matchedOrg) {
      organisationName = matchedOrg.orgName || matchedOrg.name || '';
      organisationType = matchedOrg.organisationType || matchedOrg.type || 'Public';
      industry = matchedOrg.industry || 'forest';
      department = matchedOrg.department || matchedOrg.dept || matchedOrg.departmentName || '';
    }
  }

  const uploadedBy =
    result.email ||
    result.emailAddress ||
    result.registeredEmail ||
    result.userName ||
    result.name ||
    '';

  return {
    userOrganisationId,
    organisationName,
    organisationType,
    industry,
    department,
    uploadedBy,
    ownerUserId: result.userId || '',
  };
}

export async function buildAssetCataloguePayloadWithAuth(
  authApiService: AuthApiService,
  options: Omit<BuildAssetCataloguePayloadOptions, 'organizationContext'>,
): Promise<Record<string, unknown>> {
  const organizationContext = await resolveOrganizationContext(authApiService);
  return buildAssetCataloguePayload({ ...options, organizationContext });
}

export function mapAccessPolicy(permission: string | undefined | null): string {
  if (!permission) {
    return 'OPEN';
  }
  const normalized = permission.trim();
  if (normalized === 'Open' || normalized.toUpperCase() === 'OPEN') {
    return 'OPEN';
  }
  if (normalized === 'Restricted' || normalized.toUpperCase() === 'RESTRICTED') {
    return 'RESTRICTED';
  }
  if (normalized === 'Private' || normalized.toUpperCase() === 'PRIVATE') {
    return 'PRIVATE';
  }
  return normalized.toUpperCase();
}

export function formatYearRangeForPayload(yearRange: unknown): string | null {
  if (!yearRange) {
    return null;
  }
  if (typeof yearRange === 'string') {
    return yearRange;
  }
  if (
    typeof yearRange === 'object' &&
    yearRange !== null &&
    'startYear' in yearRange &&
    'endYear' in yearRange
  ) {
    const range = yearRange as { startYear: number | string; endYear: number | string };
    return `${range.startYear} - ${range.endYear}`;
  }
  return null;
}

export function normalizeTags(tags: unknown): string[] {
  if (Array.isArray(tags)) {
    return tags;
  }
  if (!tags) {
    return [];
  }
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }
  return [String(tags)];
}

function stripAdexPrefix(value: string): string {
  const trimmed = value.trim();
  return /^adex:/i.test(trimmed) ? trimmed.replace(/^adex:/i, '') : trimmed;
}

/** Normalize a single descriptor row from API, form, or JSON. */
export function normalizeAssetDescriptorInput(desc: unknown): AssetDescriptorInput | null {
  if (!desc || typeof desc !== 'object' || Array.isArray(desc)) {
    return null;
  }

  const record = desc as Record<string, unknown>;
  const attributeNameRaw =
    record['attributeName'] ?? record['attribute_name'] ?? record['name'] ?? record['label'] ?? '';
  const parameterTypeRaw =
    record['parameterType'] ??
    record['parameter_type'] ??
    record['ParameterType'] ??
    (Array.isArray(record['type']) ? record['type'][0] : record['type']);
  const schemaTypeRaw =
    record['schemaType'] ??
    record['schema_type'] ??
    record['dataType'] ??
    record['datatype'] ??
    record['dataSchema'] ??
    record['data_schema'] ??
    '';

  const attributeName = String(attributeNameRaw ?? '').trim();
  const parameterType = stripAdexPrefix(String(parameterTypeRaw ?? '').trim());
  const schemaType =
    schemaTypeRaw != null && schemaTypeRaw !== ''
      ? stripAdexPrefix(String(schemaTypeRaw).trim())
      : '';
  const description = String(record['description'] ?? '').trim();
  const unitCode = String(record['unitCode'] ?? record['unit_code'] ?? '').trim();
  const unitText = String(record['unitText'] ?? record['unit_text'] ?? '').trim();

  if (!attributeName || !parameterType || !schemaType) {
    return null;
  }

  const normalized: AssetDescriptorInput = { attributeName, parameterType, schemaType };
  if (description) {
    normalized.description = description;
  }
  if (unitCode) {
    normalized.unitCode = unitCode;
  }
  if (unitText) {
    normalized.unitText = unitText;
  }
  return normalized;
}

function isDescriptorPayloadMap(value: unknown): value is Record<string, AssetDescriptorPayloadEntry> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) {
    return false;
  }
  return entries.every(([, entry]) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return false;
    }
    const record = entry as Record<string, unknown>;
    return (
      record['parameterType'] != null ||
      record['parameter_type'] != null ||
      record['schemaType'] != null ||
      record['schema_type'] != null ||
      record['dataSchema'] != null ||
      record['data_schema'] != null
    );
  });
}

/**
 * Normalize API / form descriptor input to a descriptor array.
 * Supports array rows, object maps, and nested dataDescriptor / dataDescriptors fields.
 */
export function normalizeAssetDescriptorList(raw: unknown): AssetDescriptorInput[] {
  if (raw == null) {
    return [];
  }

  if (Array.isArray(raw)) {
    if (
      raw.length === 1 &&
      raw[0] &&
      typeof raw[0] === 'object' &&
      !Array.isArray(raw[0]) &&
      Array.isArray((raw[0] as Record<string, unknown>)['value'])
    ) {
      return normalizeAssetDescriptorList((raw[0] as Record<string, unknown>)['value']);
    }

    return raw
      .map((desc) => normalizeAssetDescriptorInput(desc))
      .filter((desc): desc is AssetDescriptorInput => desc != null);
  }

  if (typeof raw !== 'object') {
    return [];
  }

  const source = raw as Record<string, unknown>;
  const nestedArray =
    (Array.isArray(source['dataDescriptors']) ? source['dataDescriptors'] : null) ??
    (Array.isArray(source['data_descriptors']) ? source['data_descriptors'] : null) ??
    (Array.isArray(source['dataDescriptor']) ? source['dataDescriptor'] : null);
  if (nestedArray) {
    return normalizeAssetDescriptorList(nestedArray);
  }

  const nestedMap = source['dataDescriptor'] ?? source['data_descriptor'];
  if (nestedMap && typeof nestedMap === 'object' && !Array.isArray(nestedMap)) {
    return normalizeAssetDescriptorList(nestedMap);
  }

  const dataDescriptorsField = source['dataDescriptors'];
  if (
    dataDescriptorsField &&
    typeof dataDescriptorsField === 'object' &&
    !Array.isArray(dataDescriptorsField)
  ) {
    return normalizeAssetDescriptorList(dataDescriptorsField);
  }

  const result: AssetDescriptorInput[] = [];
  for (const [key, value] of Object.entries(source)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }
    const normalized = normalizeAssetDescriptorInput({ attributeName: key, ...(value as object) });
    if (normalized) {
      result.push(normalized);
    }
  }
  return result;
}

function descriptorsToPayloadObject(
  descriptors: AssetDescriptorInput[],
): Record<string, AssetDescriptorPayloadEntry> {
  const descriptorObject: Record<string, AssetDescriptorPayloadEntry> = {};
  descriptors.forEach((desc) => {
    const entry: AssetDescriptorPayloadEntry = {
      parameterType: desc.parameterType || '',
      schemaType: desc.schemaType || '',
    };
    const description = String(desc.description ?? '').trim();
    if (description) {
      entry.description = description;
    }
    const unitCode = String(desc.unitCode ?? '').trim();
    const unitText = String(desc.unitText ?? '').trim();
    if (unitCode) {
      entry.unitCode = unitCode;
    }
    if (unitText) {
      entry.unitText = unitText;
    }
    descriptorObject[desc.attributeName] = entry;
  });
  return descriptorObject;
}

/** Collect descriptor rows for modify/update from form and catalogue sources. */
export function resolveModifyAssetDescriptors(
  formData: AssetCatalogueFormData,
  sources?: {
    assetPreviewData?: Record<string, unknown>;
    originalData?: AssetCatalogueOriginalData;
  },
): AssetDescriptorInput[] {
  const candidates: unknown[] = [
    formData.dataDescriptors,
    formData.data_descriptor,
    sources?.assetPreviewData?.['dataDescriptors'],
    sources?.assetPreviewData?.['dataDescriptor'],
    sources?.assetPreviewData?.['data_descriptor'],
    sources?.originalData?.dataDescriptors,
    sources?.originalData?.dataDescriptor,
    sources?.originalData?.data_descriptor,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeAssetDescriptorList(candidate);
    if (normalized.length > 0) {
      return normalized;
    }
  }

  return [];
}

export function buildDataDescriptorFromForm(
  formData: AssetCatalogueFormData,
  dataDescriptors?: AssetDescriptorInput[],
  originalData?: AssetCatalogueOriginalData,
  assetPreviewData?: Record<string, unknown>,
): Record<string, AssetDescriptorPayloadEntry> {
  const fromExplicit = normalizeAssetDescriptorList(dataDescriptors);
  if (fromExplicit.length > 0) {
    return descriptorsToPayloadObject(fromExplicit);
  }

  const fromResolved = resolveModifyAssetDescriptors(formData, { originalData, assetPreviewData });
  if (fromResolved.length > 0) {
    return descriptorsToPayloadObject(fromResolved);
  }

  const raw = formData.dataDescriptors ?? formData.data_descriptor;
  if (isDescriptorPayloadMap(raw)) {
    return raw;
  }

  return {};
}

function resolveResourceServerUrlForEntry(
  rs: { type?: string; name?: string; url?: string },
  urlConfig: ReturnType<typeof resolveResourceServerUrlConfig>,
): string {
  if (rs.url?.trim()) {
    return normalizeResourceServerHostUrl(rs.url);
  }
  const id = normalizeResourceServerId(rs.type || rs.name || '');
  return urlConfig[id] || '';
}

function resolveAccessTypesForEntry(rs: {
  type?: string;
  name?: string;
  accessTypes?: string[];
}): string[] {
  if (Array.isArray(rs.accessTypes) && rs.accessTypes.length > 0) {
    return rs.accessTypes.map((t) => String(t).toLowerCase());
  }
  return resolveResourceServerAccessTypes(rs.type || rs.name || '');
}

/** Infer OGC vector vs raster mode from catalogue `resourceServer[].queryTypes`. */
function inferOgcOnboardingModeFromResourceServer(
  resourceServerValue: unknown,
): 'feature' | 'raster' | null {
  const rsArray: unknown[] = Array.isArray(resourceServerValue)
    ? resourceServerValue
    : resourceServerValue
      ? [resourceServerValue]
      : [];
  for (const entry of rsArray) {
    if (entry && typeof entry === 'object') {
      const qt = (entry as Record<string, unknown>)['queryTypes'];
      if (Array.isArray(qt)) {
        if (qt.includes('FEATURE')) return 'feature';
        if (qt.includes('STAC')) return 'raster';
      }
    }
  }
  return null;
}

export function buildResourceServerArrayForPayload(
  resourceServerValue: string | any,
  originalResourceServer?: unknown,
): any[] {
  const urlConfig = resolveResourceServerUrlConfig(environment);

  if (!resourceServerValue) {
    return [
      buildResourceServerPayloadEntry('file-server', urlConfig),
      buildResourceServerPayloadEntry('ogc', urlConfig),
    ];
  }

  if (typeof resourceServerValue === 'string') {
    if (resourceServerValue.toUpperCase() === FILE_RESOURCE_SERVER_VALUE) {
      return buildResourceServerPayloadArray('file-server', urlConfig);
    }

    const resourceServerId = inferResourceServerIdFromValue(resourceServerValue);
    const ogcOnboardingMode =
      resourceServerId === 'ogc'
        ? inferOgcOnboardingModeFromResourceServer(originalResourceServer)
        : null;
    return buildResourceServerPayloadArray(resourceServerId, urlConfig, ogcOnboardingMode);
  }

  if (Array.isArray(resourceServerValue)) {
    return resourceServerValue.map((rs: any) => ({
      id: rs.id || DEFAULT_CATALOGUE_RESOURCE_SERVER_ID,
      name: rs.name || 'FILE',
      queryTypes: rs.queryTypes || ['ATTR'],
      accessTypes: resolveAccessTypesForEntry(rs),
      type: rs.type || 'file',
      url: resolveResourceServerUrlForEntry(rs, urlConfig),
    }));
  }

  if (typeof resourceServerValue === 'object') {
    const rs = resourceServerValue as { type?: string; name?: string; url?: string; id?: string };
    return [
      {
        id: rs.id || DEFAULT_CATALOGUE_RESOURCE_SERVER_ID,
        name: rs.name || 'FILE',
        queryTypes: (resourceServerValue as any).queryTypes || ['ATTR'],
        accessTypes: resolveAccessTypesForEntry(rs),
        type: rs.type || 'file',
        url: resolveResourceServerUrlForEntry(rs, urlConfig),
      },
    ];
  }

  return [];
}

function resolveDescriptions(formData: AssetCatalogueFormData): {
  shortDescription: string;
  longDescription: string;
  modelIndustry: string;
} {
  let shortDescription = formData.shortDescription || '';
  let longDescription = formData.longDescription || formData.description || '';
  let modelIndustry = formData.industry || '';

  if (!shortDescription || !longDescription || !modelIndustry) {
    try {
      const jsonSchema = formData.jsonSchema;
      if (jsonSchema) {
        const parsedSchema = typeof jsonSchema === 'string' ? JSON.parse(jsonSchema) : jsonSchema;
        shortDescription = shortDescription || parsedSchema.shortDescription || '';
        longDescription =
          longDescription || parsedSchema.longDescription || parsedSchema.description || '';
        modelIndustry = modelIndustry || parsedSchema.industry || '';
      }
    } catch (error) {
      console.warn('Error parsing jsonSchema for descriptions:', error);
    }
  }

  return { shortDescription, longDescription, modelIndustry };
}

function resolveAssetType(originalData: AssetCatalogueOriginalData | undefined, isModel: boolean): string[] {
  const rawType = originalData?.type;
  const typeArray = Array.isArray(rawType) ? rawType : rawType ? [rawType] : [];
  if (typeArray.length > 0) {
    return typeArray.map((value) => String(value));
  }
  return isModel ? ['adex:AiModel'] : ['adex:DataBank'];
}

function resolveMediaUrl(
  formData: AssetCatalogueFormData,
  originalData?: AssetCatalogueOriginalData,
): string | undefined {
  const fromForm = typeof formData.sampleDataUrl === 'string' ? formData.sampleDataUrl.trim() : '';
  if (fromForm) {
    return fromForm;
  }
  const fromOriginal = originalData?.mediaURL ?? originalData?.media_url ?? originalData?.sampleDataUrl;
  if (typeof fromOriginal === 'string' && fromOriginal.trim()) {
    return fromOriginal.trim();
  }
  return undefined;
}

/** Canonical catalogue payload (add-dataset shape). Pass assetId for modify/update flows. */
export function buildAssetCataloguePayload(
  options: BuildAssetCataloguePayloadOptions,
): Record<string, unknown> {
  const {
    formData,
    isModel,
    organizationContext,
    assetId,
    originalData,
    dataDescriptors,
    assetPreviewData,
    includeModelDataSourceUrl = true,
    defaultModelMediaUrl = 'https://iudx.io/',
  } = options;

  const org = organizationContext;
  const original: AssetCatalogueOriginalData = originalData ?? {};
  const { shortDescription, longDescription, modelIndustry } = resolveDescriptions(formData);
  const tags = normalizeTags(formData.tags);

  const organizationId =
    org.userOrganisationId || original.organizationId || '6e5bd047-9eb8-4bba-b72e-f57ce3e23ce6';
  const organizationName = org.organisationName || original.organization || 'Rajasthan Forestry';
  const organizationType = org.organisationType || original.organizationType || 'Public';
  const industry = formData.industry || modelIndustry || org.industry || original.industry || 'forest';
  const department = org.department || original.department || '';
  const uploadedBy =
    org.uploadedBy || original.uploadedBy || 'Rajasthan Forest Department Data Provider';
  const ownerUserId = org.ownerUserId || original.ownerUserId || original.owner_user_id || '';

  if (isModel) {
    const payload: Record<string, unknown> = {
      type: resolveAssetType(original, true),
      name: formData.title || original.name || '',
      description: longDescription,
      shortDescription,
      label: formData.title || original.name || '',
      tags,
      accessPolicy: mapAccessPolicy(formData.permission),
      apdURL: resolveCatalogueApdUrl(environment.basePathUrl),
      organizationId,
      organizationType,
      department,
      modelType: formData.datasetType || formData.modelType || original.modelType || '',
      fileFormat: (formData.fileFormat || original.fileFormat || '').toString().toLowerCase(),
      industry,
      uploadedBy,
      license: FormatService.resolveCatalogueLicenseForApi(
        formData.license,
        formData.otherLicense,
        'MIT',
      ),
      fileSize: original.fileSize || original.file_size || '15MB',
    };

    const mediaURL = resolveMediaUrl(formData, original);
    if (mediaURL) {
      payload['mediaURL'] = mediaURL;
    } else if (includeModelDataSourceUrl && defaultModelMediaUrl) {
      payload['mediaURL'] = defaultModelMediaUrl;
    }

    if (assetId) {
      payload['id'] = assetId;
    }

    return payload;
  }

  const resourceServerArray = buildResourceServerArrayForPayload(
    formData.resourceServer || original.resourceServer || original.resource_server || '',
    original.resourceServer ?? original.resource_server,
  );
  const dataDescriptor = buildDataDescriptorFromForm(
    formData,
    dataDescriptors,
    original,
    assetPreviewData,
  );

  const payload: Record<string, unknown> = {
    '@context': 'https://voc.forest-stack.iudx.io/',
    type: resolveAssetType(original, false),
    name: formData.title || original.name || '',
    shortDescription: shortDescription || original.shortDescription || '',
    description: longDescription || original.description || '',
    tags,
    concepts: formData.concepts || original.concepts || ['', 'Administration'],
    accessPolicy: mapAccessPolicy(formData.permission),
    apdURL: resolveCatalogueApdUrl(environment.basePathUrl),
    organization: organizationName,
    organizationId,
    organizationType,
    industry,
    uploadedBy,
    verifiedBy: original.verifiedBy || '-',
    ownerUserId,
    license: FormatService.resolveCatalogueLicenseForApi(
      formData.license,
      formData.otherLicense,
      'Open Government License, India',
    ),
    yearRange:
      formatYearRangeForPayload(formData.yearRange) ||
      formatYearRangeForPayload(original.yearRange) ||
      original.yearRange ||
      '2020 - 2021',
    geoCoverage: formData.geoCoverage || original.geoCoverage || original.geo_coverage || 'Test',
    uploadFrequency:
      formData.uploadFrequency || original.uploadFrequency || original.upload_frequency || 'One-time',
    dataBankType:
      formData.datasetType || original.dataBankType || original.datasetType || 'Structured',
    fileFormat: (formData.fileFormat || original.fileFormat || 'csv').toString().toLowerCase(),
    resourceServer: resourceServerArray,
    ...(Object.keys(dataDescriptor).length > 0 ? { dataDescriptor } : {}),
  };

  const dataSample = formData.dataSample || original.dataSample || original.data_sample;
  if (dataSample != null && dataSample !== '') {
    payload['dataSample'] = dataSample;
  }

  if (department) {
    payload['department'] = department;
  }

  const mediaURL = resolveMediaUrl(formData, original);
  if (mediaURL) {
    payload['mediaURL'] = mediaURL;
  }

  if (assetId) {
    payload['id'] = assetId;
  }

  return payload;
}
